/**
 * How big the HUD is drawn, for every mode and both skins at once.
 *
 * The bounds and the default live here rather than in the knob, the way
 * [lookSensitivity](../../core/input/lookSensitivity.ts) owns its own: a knob names a quantity and
 * hands a value to whoever owns it, and owning the range in two places is how a slider comes to
 * offer a value the thing it drives clamps away.
 *
 * The number is a multiplier on the frame's own font size, and every length in every element sheet
 * is stated in `em` against it. So the whole HUD is laid out again at the new size rather than
 * being a picture of the old size stretched: the text stays as crisp at 1.6 as at 0.7, and a
 * padding that keeps a corner readout off a rounded phone corner grows with the readout it is
 * keeping clear. A transform would have been one line and would have blurred every glyph.
 *
 * The screen-anchored elements are deliberately outside that. A vignette that fills the frame has
 * no size to scale, and a bearing chevron is placed in `vmin` off the middle of the picture, which
 * is a fact about the screen rather than about how big the player wants the numbers.
 */

import { clamp } from '@/sim/math';

/** The size the HUD was designed at. Every element sheet's `em` values are drawn against this. */
export const DEFAULT_HUD_SCALE = 1;

/**
 * As small as the corners stay legible.
 *
 * Below this the ammunition count is under ten pixels tall on a phone, which is a readout nobody
 * can use and a setting that only looks like it offers something.
 */
export const MIN_HUD_SCALE = 0.7;

/**
 * As large as the corners fit without meeting in the middle.
 *
 * Set against the widest thing either skin draws, which is the conventional squad roster at the
 * bottom left beside the conventional ammunition block at the bottom right. Past this the two
 * columns overlap on a 16:9 window, and a HUD that covers the crosshair is worse than a small one.
 */
export const MAX_HUD_SCALE = 1.6;

let scale = DEFAULT_HUD_SCALE;

export function hudScale(): number {
  return scale;
}

/** Clamped here as well as in the settings layer: this is the size the HUD is actually drawn at. */
export function setHudScale(value: number): void {
  scale = clamp(value, MIN_HUD_SCALE, MAX_HUD_SCALE);
}
